import CountPip from '@orn/shared-web/components/CountPip';
import { BellIcon } from '@orn/shared-web/icons';
import { cx } from '@orn/shared-web/utils/cx';
import isPlainLeftClick from '@orn/shared-web/utils/dom/isPlainLeftClick';
import * as React from 'react';
import { Link as RouterLink } from 'react-router-dom';

import { PATHS } from '../../paths';

interface INotificationBellButtonProps {
  /**
   * Unread alert count. The pip is hidden at zero and clamps its display at
   * "9+"; the accessible name always carries the true number.
   */
  unreadCount: number;
  /** Whether the full Notification Center is the surface currently on screen. */
  current?: boolean;
  /**
   * Opens the center in place. A plain left-click takes this path; every
   * modified click still follows the href to `/alerts`, whose route gate lands
   * on the same center.
   */
  onOpen?: () => void;
}

/**
 * Bell entry-point to the Notification Center, with an unread-count pip
 * overlaying the icon (Figma node `6383:472847`). Presentational — the caller
 * (`MainSidebar`) resolves the `feat-alerts-center` gate, supplies the count,
 * and decides what opening means; this component renders unconditionally when
 * mounted.
 *
 * A link rather than a button, so the destination supports middle-click and
 * open-in-new-tab like every other nav destination in this chrome. The center
 * is a layer of the assistant pane rather than a route, so a plain left-click
 * opens it in place via `onOpen` — at `xl+` that toggles the full center in
 * the side column without touching the content column; below `xl` it opens the
 * center and navigates to `/assistant`, the pane's route. Modified clicks skip
 * the handler and open `/alerts` in a new tab, where the route gate does the
 * same hand-off.
 *
 * `unreadCount` is the `unread` bucket of the work-item status counts the
 * center's own reads use, so the badge and the center cannot disagree. It
 * counts unread only — the center's "Need attention" header covers unread
 * *and* read, so the two numbers are deliberately different. Every alert the
 * center lists is a stored row, phone verification included, so nothing it
 * shows is missing from the count.
 *
 * Native `title` over the `Tooltip` primitive deliberately: `Tooltip`'s
 * react-aria trigger passes its props through context that a `RouterLink`
 * does not consume, so the tooltip would never open. The broader migration
 * off `title` is tracked in ORN-657.
 */
const NotificationBellButton = ({ unreadCount, current, onOpen }: INotificationBellButtonProps) => {
  const handleClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    if (!onOpen || !isPlainLeftClick(event)) {
      return;
    }

    event.preventDefault();
    onOpen();
  };

  return (
    <RouterLink
      to={PATHS.ALERTS}
      onClick={handleClick}
      title="Alerts"
      aria-label={unreadCount > 0 ? `Alerts, ${unreadCount} unread` : 'Alerts'}
      aria-current={current ? 'page' : undefined}
      className={cx(
        'outline-focus-ring relative flex size-9 shrink-0 items-center justify-center rounded-full transition duration-100 ease-linear focus-visible:outline-2 focus-visible:outline-offset-2',
        current
          ? 'bg-active text-fg-quaternary_hover'
          : 'text-fg-quaternary hover:bg-primary_hover hover:text-fg-quaternary_hover'
      )}
    >
      <BellIcon aria-hidden="true" className="size-5 shrink-0" />
      <CountPip count={unreadCount} />
    </RouterLink>
  );
};

export default NotificationBellButton;
